/**
 * Labels and formatting for the five engagement questions.
 *
 * Kept apart from the component for the same reason as the growth formatters:
 * the wording is the part worth testing, and it should not require rendering a
 * page to check it.
 *
 * Each row is phrased as the question it answers, not as the metric name. The
 * threshold is what turns a number into a decision, so it travels with it.
 */

export const ENGAGEMENT_QUESTIONS = {
  budget_activation: {
    title: 'Do new users record anything in week one?',
    metric: 'Four or more budget entries within seven days; only users at least seven days old',
    // Why this threshold and not another: below it, the tracker is being
    // opened but not adopted, and nothing downstream can work.
    rationale: 'Below this, people look at the tracker but never populate it.',
  },
  second_month: {
    title: 'Do they come back for a second month?',
    metric: 'Recorded activity between days 28 and 60; only users at least 60 days old',
    rationale: 'Monthly budgeting only compounds if the second month happens.',
  },
  tracker_to_guardrail: {
    title: 'Does the tracker lead anywhere?',
    metric: 'Of the people who track spending, how many subsequently run a protected decision',
    rationale:
      'This is the one that decides whether the tracker is a bridge to the product we sell, or a separate app living inside it.',
  },
  weekly_share: {
    title: 'Does anyone show the app to someone else?',
    metric: 'Weekly card shares as a share of weekly active users',
    rationale: 'Below this, growth has to be bought rather than earned.',
  },
  paid_conversion: {
    title: 'Do profile users reach a verified store checkout?',
    metric: 'Production store checkout verified after recorded profile completion; cash receipts are measured separately',
    rationale: 'Trials, sandbox purchases and Premium flags do not establish collected revenue.',
  },
};

export const VERDICT_PRESENTATION = {
  not_measurable: {label: 'Not measurable', scheme: 'gray'},
  above: {label: 'Above threshold', scheme: 'green'},
  below: {label: 'Below threshold', scheme: 'red'},
  insufficient_data: {label: 'Not enough data yet', scheme: 'gray'},
};

/**
 * A rate that is null stays absent. It is never rendered as 0%, because a
 * missing measurement and a measured zero lead to opposite decisions.
 */
export const formatRate = value =>
  value === null || value === undefined ? '—' : `${Math.round(value * 1000) / 10}%`;

export const formatThreshold = value =>
  value === null || value === undefined ? '—' : `${Math.round(value * 1000) / 10}%`;

export const describeSample = ({numerator, denominator, readable, minimumCohort}) => {
  if (!denominator) return 'No one qualifies for this measurement yet.';
  if (!readable) {
    return `${numerator} of ${denominator} — needs ${minimumCohort} before the rate means anything.`;
  }
  return `${numerator} of ${denominator}`;
};

export const describeWindow = ({since, until} = {}) => {
  if (!since && !until) return 'All users, since launch';
  const format = value => new Date(value).toLocaleDateString();
  if (since && until) return `Signed up ${format(since)} – ${format(until)}`;
  if (since) return `Signed up after ${format(since)}`;
  return `Signed up before ${format(until)}`;
};
